import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback, useContext } from "react";
import { enqueueSnackbar } from "notistack";
import { useTextDomainContext, useUser } from "@/app/providers";

interface UseDeleteAllNotificationsVariables {
  userId?: string;
  email?: string;
}

interface UseDeleteAllNotificationsReturn {
  deleteAllNotifications: (
    variables?: UseDeleteAllNotificationsVariables,
  ) => void;
  deletingAll: boolean;
}

/**
 * Custom hook to delete all notifications.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @param {UseDeleteAllNotificationsVariables} [defaultVariables] - Default variables to use if not passed on call.
 */
export const useDeleteAllNotifications = (
  onCompleted: (_data: any) => void = () => {},
  onError: (_err: any) => void = () => {},
  defaultVariables?: UseDeleteAllNotificationsVariables,
): UseDeleteAllNotificationsReturn => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();

  const [deleteAllNotifications, { loading }] = useMutation<any>(
    mutations.deleteAllNotifications,
    {
      onCompleted: (_data) => {
        onCompleted(_data);

        if (
          (_data as any)?.deleteAllNotifications?.success ||
          (_data as any)?.comDeleteAllNotifications?.success
        ) {
          enqueueSnackbar(gettext("All notifications deleted successfully"), {
            variant: "success",
          });
        }
      },
      onError: (_err) => {
        onError(_err);

        enqueueSnackbar("Delete All Notifications: " + _err.message, {
          variant: "error",
        });
      },
      refetchQueries: [
        "getAllNotifications",
        "getNewNotifications",
        "comGetAllNotifications",
        "comGetNewNotifications",
      ],
    },
  );

  const deleteAll = useCallback(
    (variables?: UseDeleteAllNotificationsVariables) => {
      const targetUserId = variables?.userId ?? defaultVariables?.userId;
      const targetEmail =
        variables?.email ??
        defaultVariables?.email ??
        (targetUserId ? undefined : user?.email);

      deleteAllNotifications({
        variables: {
          userId: targetUserId,
          email: targetEmail,
        },
      }).then();
    },
    [deleteAllNotifications, defaultVariables, user?.email],
  );

  return {
    deleteAllNotifications: deleteAll,
    deletingAll: loading,
  };
};
